import React, { useEffect, useState, useMemo } from 'react'
import { Hotel, ShoppingCart, X, Calendar, Leaf, BarChart3, ArrowLeft, User, Search, Plus, Edit2, CheckCircle, AlertTriangle, Clock, Users, MapPin, ArrowRight, Compass, Sparkles, ShieldCheck, UserCheck, UserPlus, ChevronRight, CreditCard, CalendarClock } from 'lucide-react'
import { FaCheck, FaTimes } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { getAgentMarkupBreakdown, resolveDefaultCommissionPct } from '../utils/agentCommissionCart'
import { OFF_MARKET_DEFAULT_COMMISSION_PCT } from '../utils/offMarketSpecialCommission'
import { getCartItemPaymentType, formatPaymentDate } from '../utils/paymentPolicyUtils'
import { formatPrice } from '../utils/currencyUtils'
import { getHotelStaySourcePresentation } from '../utils/staySourceLabel'
import HotelCartItemPricing from './checkout/HotelCartItemPricing'

const ClientSelectionSidebar = ({ isOpen, onClose, onSelect, clients, selectedClientId, itemName, onShowRegistration, searchTerm, onSearchChange }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[70]"
        />
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 250 }}
          className="fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl z-[80] overflow-hidden flex flex-col border-l border-gray-200"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-100 relative overflow-hidden" style={{ borderTopWidth: 2, borderTopColor: '#111827' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[9px] font-poppinsLight text-gray-400 uppercase tracking-[0.3em] mb-1.5 flex items-center gap-2">
                  <UserCheck className="h-3 w-3" /> Assign Client Per Unit
                </p>
                <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-base leading-tight truncate max-w-[240px]">{itemName}</h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-900 transition-all border border-gray-200 hover:border-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Search Input */}
            <div className="relative mt-2">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Find client by name or email..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 text-xs font-poppinsLight focus:bg-white focus:border-gray-900 outline-none transition-all duration-200"
              />
            </div>
          </div>

          {/* client List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {clients.length > 0 ? (
              clients.map((client) => (
                <motion.div
                  key={client._id || client.id}
                  whileHover={{ x: 4 }}
                  onClick={() => onSelect(client)}
                  className={`p-3.5 border cursor-pointer transition-all duration-300 flex items-center justify-between group ${selectedClientId === (client._id || client.id)
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-100 bg-gray-50 hover:border-gray-300 hover:bg-white'
                    }`}
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className={`w-9 h-9 flex items-center justify-center transition-colors ${selectedClientId === (client._id || client.id) ? 'bg-white/10' : 'bg-white border border-gray-200'
                      }`}>
                      <User className={`h-4 w-4 ${selectedClientId === (client._id || client.id) ? 'text-white' : 'text-gray-400'}`} />
                    </div>
                    <div className="min-w-0">
                      <p className={`font-poppinsRegular text-sm ${selectedClientId === (client._id || client.id) ? 'text-white' : 'text-gray-900'}`}>
                        {client.firstName} {client.lastName}
                      </p>
                      <p className={`text-[10px] uppercase tracking-wide font-poppinsLight truncate ${selectedClientId === (client._id || client.id) ? 'text-white/60' : 'text-gray-400'}`}>
                        {client.email}
                      </p>
                    </div>
                  </div>
                  {selectedClientId === (client._id || client.id) ? (
                    <CheckCircle className="h-4 w-4 text-white shrink-0" />
                  ) : (
                    <ArrowRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                  )}
                </motion.div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-8 opacity-40">
                <UserPlus className="h-10 w-10 mb-4 text-gray-300" />
                <p className="text-[10px] font-poppinsLight uppercase tracking-[0.3em] text-gray-400">No guests found</p>
              </div>
            )}
          </div>

          <div className="p-5 border-t border-gray-100">
            <button
              onClick={onShowRegistration}
              className="w-full py-3.5 bg-gray-900 text-white text-[10px] font-poppinsRegular uppercase tracking-[0.25em] hover:bg-black transition-all duration-200 flex items-center justify-center gap-3"
            >
              <UserPlus className="h-3.5 w-3.5" />
              Register New Guest
            </button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
)

const CartReviewStep = ({
  profile,
  selectedCustomerForItem,
  agentCustomerDetails,
  summary,
  deleteCartItemLoading,
  deleteAllCartDeleteLoading,
  onDeleteItem,
  onClearAll,
  onCustomerSelect,
  onNextStep,
  onNextStepExperiences,
  onNavigateHotels,
  onShowClientRegistration,
  agentMarkupSettings = { loaded: false, markupEnabled: false, markupPercentage: 10 },
  paymentFlowByItemId = {}
}) => {
  const navigate = useNavigate();
  const [activeSidebarItem, setActiveSidebarItem] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const experienceItems = profile?.filter(item => item.type === 'experience') || []
  const hotelItems = profile?.filter(item => item.type !== 'experience') || []

  const experienceCurrencyTotals = useMemo(() => {
    const m = {}
    experienceItems.forEach((item) => {
      const cur = item.currency || 'USD'
      const t = Number(item.totalWithProcessingFees ?? item.price ?? 0) || 0
      m[cur] = (m[cur] || 0) + t
    })
    return m
  }, [experienceItems])
  const unavailableHotelItems = hotelItems.filter(
    (item) => item?.availabilityStatus?.isAvailable === false
  )

  useEffect(() => {
    if (unavailableHotelItems.length === 0) return
    console.warn('[CartReviewStep] Unavailable hotel items in cart', unavailableHotelItems.map((item) => ({
      itemId: item?.id,
      hotelId: item?.hotelId,
      reason: item?.availabilityStatus?.reason
    })))
  }, [unavailableHotelItems.length])

  const handleOpenSidebar = (itemId) => {
    setActiveSidebarItem(itemId)
    setSearchTerm('')
  }

  const handleCloseSidebar = () => {
    setActiveSidebarItem(null)
  }

  const handleSelectClient = (client) => {
    onCustomerSelect(activeSidebarItem, client._id, client)
    handleCloseSidebar()
  }

  const filteredClients = agentCustomerDetails.filter(client =>
    `${client.firstName} ${client.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const ItemCard = ({ item, type }) => {
    const selectedGuest = selectedCustomerForItem[item.id]
    const isHotelUnavailable = type === 'hotel' && item?.availabilityStatus?.isAvailable === false
    const availabilityReason = item?.availabilityStatus?.reason || 'unavailable'
    const [commissionPopupOpen, setCommissionPopupOpen] = useState(false)
    const paymentMode = paymentFlowByItemId[String(item.id)]
    const isImmediateStripe = paymentMode === 'immediate'
    const paymentBadgeClass = isImmediateStripe
      ? 'bg-rose-50 border-rose-100 text-rose-700'
      : paymentMode === 'scheduled'
        ? 'bg-gradient-to-r from-blue-950 to-slate-900 border-slate-800 text-white'
        : 'bg-slate-100 border-slate-200 text-slate-600'
    const paymentBadgeText = isImmediateStripe
      ? 'Immediate'
      : paymentMode === 'scheduled'
        ? 'Scheduled'
        : 'Flow Resolving'

    const staySrc = type === 'hotel' ? getHotelStaySourcePresentation(item) : null

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98 }}
        className="group relative bg-white border border-gray-200 overflow-hidden transition-all duration-300 hover:border-gray-400"
        style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
      >
        <div className="flex flex-col lg:flex-row">
          {/* Image Section */}
          <div className="w-full lg:w-64 h-48 lg:h-auto overflow-hidden relative">
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${type === 'experience' ? 'from-slate-900/30' : 'from-blue-900/30'} via-transparent to-transparent`} />

            {/* Type + stay-source badges (GDS vs off-market catalogue) */}
            <div className="absolute top-4 left-4 flex flex-col gap-1.5 max-w-[min(100%,12rem)]">
              <div className={`px-2.5 py-1 backdrop-blur-md border border-white/20 flex items-center gap-2 ${type === 'experience' ? 'bg-slate-500/20 text-white' : 'bg-emerald-500/20 text-white'}`}>
                {type === 'experience' ? <Sparkles className="h-3 w-3" /> : <Hotel className="h-3 w-3" />}
                <span className="text-[9px] font-poppinsLight uppercase tracking-[0.3em]">{type}</span>
              </div>
              {staySrc && (
                <div
                  className={`px-2.5 py-1 rounded-lg backdrop-blur-md border text-[8px] font-black uppercase tracking-widest shadow-sm ${staySrc.variant === 'offMarket'
                      ? 'bg-amber-500/90 text-amber-950 border-amber-200/60'
                      : 'bg-slate-900/75 text-slate-50 border-white/25'
                    }`}
                >
                  {staySrc.variant === 'offMarket' ? 'Off-market' : 'GDS hotel'}
                </div>
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 p-4 lg:p-5 flex flex-col">
            <div className="flex justify-between items-start mb-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Sparkles key={s} className="h-2 w-2 text-amber-400" />
                    ))}
                  </div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{item.category}</span>
                </div>
                <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 leading-tight text-base">
                  {item.title}
                </h3>
                {staySrc && (
                  <p className="mt-1.5 text-[10px] font-semibold flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className={staySrc.variant === 'offMarket' ? 'text-amber-800 uppercase tracking-wide' : 'text-slate-600 uppercase tracking-wide'}>
                      {staySrc.pillText}
                    </span>
                    {staySrc.detailText ? (
                      <span className="text-slate-500 font-medium normal-case tracking-normal max-w-full truncate">
                        {staySrc.detailText}
                      </span>
                    ) : null}
                  </p>
                )}
                {item.location && (
                  <p className="flex items-center text-xs text-slate-500 font-medium">
                    <MapPin className="h-3 w-3 mr-1.5 text-slate-400" />
                    {typeof item.location === 'object'
                      ? `${item.location.city || ''}, ${item.location.country || ''}`.trim().replace(/^,\s*/, '')
                      : item.location}
                  </p>
                )}
              </div>
              <button
                onClick={() => onDeleteItem(item.id)}
                disabled={deleteCartItemLoading}
                className={`p-2 rounded-xl transition-all duration-300 ${isHotelUnavailable
                    ? 'text-rose-600 bg-rose-50 hover:bg-rose-100 hover:text-rose-700 border border-rose-300 shadow-[0_0_15px_rgba(244,63,94,0.45)] ring-2 ring-rose-500/10 animate-pulse'
                    : 'text-slate-300 hover:text-rose-500 hover:bg-rose-50'
                  }`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {isHotelUnavailable && (
              <div className="mb-4 p-3 border border-amber-200 bg-amber-50 text-amber-800">
                <p className="text-[10px] font-poppinsRegular uppercase tracking-[0.25em]">Rate unavailable</p>
                <p className="text-xs mt-1">
                  Your current item is expired. Remove that and select new room/rate from{' '}
                  <button
                    onClick={() => {
                      const cIn = item.checkInDate || item.searchParams?.checkIn || '';
                      const cOut = item.checkOutDate || item.searchParams?.checkOut || '';
                      const ads = item.searchParams?.adults || item.maxAdults || 1;
                      const chs = item.searchParams?.children !== undefined ? item.searchParams?.children : (item.maxChildren || 0);
                      const cur = item.currency || item.searchParams?.currency || 'USD';
                      navigate(`/hotel/${item.hotelId}?checkInDate=${cIn}&checkOutDate=${cOut}&adults=${ads}&children=${chs}&currency=${cur}`);
                    }}
                    className="underline font-bold text-amber-900 hover:text-amber-950 transition-colors"
                  >
                    here
                  </button>
                  .
                </p>
              </div>
            )}

            {/* Guest Selection Status */}
            <div className={`mb-4 p-4 border transition-all duration-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${selectedGuest
              ? 'bg-gray-50 border-gray-200'
              : 'bg-rose-50 border-rose-200'
              }`}>
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${selectedGuest
                  ? 'bg-gray-900'
                  : 'bg-rose-500 animate-pulse'
                  }`}>
                  {selectedGuest ? <ShieldCheck className="h-5 w-5 text-white" /> : <UserPlus className="h-5 w-5 text-white" />}
                </div>
                <div className="min-w-0">
                  <p className={`text-[9px] font-poppinsLight uppercase tracking-[0.25em] mb-0.5 ${selectedGuest ? 'text-gray-400' : 'text-rose-400'}`}>
                    {selectedGuest ? 'Assigned Client' : 'Requires Assignment'}
                  </p>
                  <p className={`text-sm font-poppinsRegular leading-tight ${selectedGuest ? 'text-gray-900' : 'text-rose-900'}`}>
                    {selectedGuest ? `${selectedGuest.firstName} ${selectedGuest.lastName}` : 'Client Profile Missing'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleOpenSidebar(item.id)}
                className={`w-full sm:w-auto px-4 sm:px-5 py-2.5 text-[10px] font-poppinsRegular uppercase tracking-[0.2em] transition-all duration-200 whitespace-nowrap ${selectedGuest
                  ? 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50'
                  : 'bg-rose-600 text-white hover:bg-rose-700'
                  }`}
              >
                {selectedGuest ? 'Change' : 'Assign Client'}
              </button>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3 pb-4 border-b border-slate-100">
              {type === 'experience' ? (
                <>
                  <div className="space-y-0.5">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Duration</span>
                    <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <Clock className="h-3 w-3 text-slate-400" />
                      {typeof item.duration === 'object'
                        ? `${item.duration.days ? item.duration.days + 'd ' : ''}${item.duration.hours ? item.duration.hours + 'h ' : ''}${item.duration.minutes ? item.duration.minutes + 'm' : ''}`.trim()
                        : item.duration}
                    </p>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Participants</span>
                    <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <Users className="h-3 w-3 text-slate-400" />
                      {item.participantCount} Guests
                    </p>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Scheduled</span>
                    <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <Calendar className="h-3 w-3 text-slate-400" />
                      {item.selectedDate}
                    </p>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Difficulty</span>
                    <p className="text-xs font-bold text-slate-700 capitalize">{item.difficulty || 'Bespoke'}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-0.5">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Room Type</span>
                    <p className="text-xs font-bold text-slate-700 truncate">{item.bedType?.type || "N/A"}</p>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Nights</span>
                    <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <Calendar className="h-3 w-3 text-slate-400" />
                      {item.nights} Nites
                    </p>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Occupancy</span>
                    <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <Users className="h-3 w-3 text-slate-400" />
                      {item.maxAdults || 0} Adults, {item.maxChildren || 0} Children
                    </p>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Rate Plan</span>
                    <p className="text-xs font-bold text-slate-700 truncate">{item.ratePlanName}</p>
                  </div>
                </>
              )}
            </div>

            {/* Impact and Investment */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mt-auto">
              <div className="flex gap-2">
                {item.environmentalImpact != null && (
                  <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-100 flex items-center gap-2">
                    <Leaf className="h-3 w-3 text-emerald-500" />
                    <div>
                      <p className="text-[7px] font-poppinsLight text-emerald-600 uppercase tracking-[0.2em]">Eco Impact</p>
                      <p className="text-[9px] font-poppinsRegular text-gray-700">
                        {parseFloat(item.environmentalImpact?.carbonEmission ?? 0).toFixed(2)} Kg
                      </p>
                    </div>
                  </div>
                )}
                {type === 'hotel' && item.rateDetails?.cancellationPolicy?.CancelPenalty?.[0] && (
                  <div className={`px-3 py-1.5 border flex items-center gap-2 ${item.rateDetails.cancellationPolicy.CancelPenalty[0].Refundable
                    ? 'bg-blue-50 border-blue-100'
                    : 'bg-amber-50 border-amber-100'
                    }`}>
                    {item.rateDetails.cancellationPolicy.CancelPenalty[0].Refundable ? (
                      <CheckCircle className="h-3 w-3 text-blue-500" />
                    ) : (
                      <AlertTriangle className="h-3 w-3 text-amber-500" />
                    )}
                    <div>
                      <p className="text-[7px] font-poppinsLight uppercase tracking-[0.2em] text-gray-400">Policy</p>
                      <p className={`text-[9px] font-poppinsRegular ${item.rateDetails.cancellationPolicy.CancelPenalty[0].Refundable ? 'text-blue-700' : 'text-amber-700'}`}>
                        {item.rateDetails.cancellationPolicy.CancelPenalty[0].Refundable ? 'Refundable' : 'Fixed'}
                      </p>
                    </div>
                  </div>
                )}
                {type === 'hotel' && (
                  <div className={`px-3 py-1.5 border flex items-center gap-2 ${paymentBadgeClass}`}>
                    <Clock className="h-3 w-3" />
                    <div>
                      <p className="text-[7px] font-poppinsLight uppercase tracking-[0.2em] text-gray-400">Payment</p>
                      <p className="text-[9px] font-poppinsRegular">{paymentBadgeText}</p>
                    </div>
                  </div>
                )}
              </div>

              {type === 'hotel' ? (
                <HotelCartItemPricing item={item} />
              ) : (
                <div className="text-right">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Investment</p>
                  <p className="text-2xl typography-section-title font-black text-slate-900">
                    <span className="text-xs font-poppinsRegular mr-1">{item.currency}</span>
                    {Number(
                      Number(item.totalPrice ?? 0) ||
                      (Number(item.pricePerPerson ?? 0) * Number(item.participantCount ?? 1)) ||
                      Number(item.price ?? 0) ||
                      0
                    ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              )}
            </div>

            {/* Agent Commission Breakdown — hotel items (persisted agentCommission + profile fallback) */}
            {type === 'hotel' && (() => {
              const ac = item.agentCommission;
              const show = ac || agentMarkupSettings.markupEnabled;
              if (!show) return null;
              const baselinePct = item.isOffMarketStay
                ? OFF_MARKET_DEFAULT_COMMISSION_PCT
                : resolveDefaultCommissionPct({
                    defaultCommissionPct: ac?.defaultCommissionPct,
                    rateSource: item.rateSource,
                    isOffMarketStay: item.isOffMarketStay === true
                  });
              const baseline =
                ac?.markupBaselinePct != null && !Number.isNaN(Number(ac.markupBaselinePct))
                  ? Number(ac.markupBaselinePct)
                  : baselinePct;
              const pctRaw = ac?.markupPercentage != null ? ac.markupPercentage : agentMarkupSettings.markupPercentage;
              const pct = Number(pctRaw);
              const base = ac?.baseBookingTotal != null ? ac.baseBookingTotal : (item.totalWithProcessingFees || 0);
              const enabledAtAdd = ac ? ac.markupFeatureEnabledAtAdd !== false : agentMarkupSettings.markupEnabled;
              const round2 = (n) => Math.round(Number(n) * 100) / 100;
              const agentInc =
                ac?.offMarketStayMarkup != null && !Number.isNaN(Number(ac.offMarketStayMarkup))
                  ? Number(ac.offMarketStayMarkup)
                  : ac?.agentMarkupIncreasePct != null && !Number.isNaN(Number(ac.agentMarkupIncreasePct))
                    ? Number(ac.agentMarkupIncreasePct)
                    : Math.max(0, Math.round((pct - baseline) * 10) / 10);

              let defaultCommissionAmount;
              let totalCommissionAmount;
              let incrementalPct;
              let incrementalAmount;
              let youPay;

              if (ac) {
                defaultCommissionAmount =
                  ac.defaultCommissionAmount != null
                    ? Number(ac.defaultCommissionAmount)
                    : round2((base * baselinePct) / 100);
                totalCommissionAmount =
                  ac.totalCommissionAmount != null ? Number(ac.totalCommissionAmount) : round2((base * pct) / 100);
                incrementalPct = agentInc;
                incrementalAmount =
                  ac.markupAmount != null ? Number(ac.markupAmount) : (enabledAtAdd ? round2((base * incrementalPct) / 100) : 0);
                youPay = ac.totalAgentPays != null ? Number(ac.totalAgentPays) : round2(base + incrementalAmount);
              } else {
                const b = getAgentMarkupBreakdown(base, pctRaw, !!enabledAtAdd, {
                  defaultCommissionPct: baseline
                });
                defaultCommissionAmount = b.defaultCommissionAmount;
                totalCommissionAmount = b.totalCommissionAmount;
                incrementalPct = b.incrementalPct;
                incrementalAmount = b.incrementalAmount;
                youPay = b.totalAgentPays;
              }

              const om = item.offMarketCommission;
              const fmt = (n) => (item.currency || 'USD') + ' ' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return (
                <>
                  {/* Compact commission button */}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setCommissionPopupOpen(true)}
                      className="w-full text-left border border-gray-200 bg-gray-50 hover:bg-gray-100 overflow-hidden transition-all group/comm"
                    >
                      <div className="px-3 pt-2.5 pb-2">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-poppinsRegular uppercase tracking-[0.25em] text-gray-500">Your commission</span>
                          <span className="px-1.5 py-0.5 text-[9px] font-poppinsLight border border-gray-300 bg-white text-gray-500">Advisors only</span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-xs text-slate-500">{pct}% · estimated earnings</span>
                          <span className="text-base font-bold text-slate-800 tabular-nums">{fmt(totalCommissionAmount)}</span>
                        </div>
                        {item.isOffMarketStay && (
                          <p className="mt-1 text-[10px] font-semibold text-amber-800">
                            Off-market stay
                            {om?.totalPct != null ? ` · hotel min. ${Number(om.totalPct).toFixed(1)}%` : ''}
                          </p>
                        )}
                        {agentInc > 0 && (
                          <p className="mt-0.5 text-[10px] font-semibold text-amber-800 tabular-nums">
                            {item.isOffMarketStay
                              ? `+${agentInc.toFixed(1)}% off-market stay markup (above ${baseline.toFixed(1)}% floor)`
                              : `+${agentInc.toFixed(1)}% markup (above ${baseline.toFixed(1)}% floor)`}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-200/70 group-hover/comm:bg-gray-300/60 transition-colors border-t border-gray-200">
                        <span className="text-[10px] font-poppinsLight text-gray-600">View commission details</span>
                        <ChevronRight className="w-3 h-3 text-gray-500" />
                      </div>
                    </button>
                  </div>

                  {/* Commission details popup */}
                  {commissionPopupOpen && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={() => setCommissionPopupOpen(false)}>
                      <div className="bg-white shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        {/* Popup header */}
                        <div className="flex items-start justify-between px-5 py-4 bg-gray-50 border-b border-gray-200" style={{ borderTopWidth: 2, borderTopColor: '#111827' }}>
                          <div>
                            <p className="text-[9px] font-poppinsLight uppercase tracking-[0.3em] text-gray-400 mb-0.5">Advisors only · Commission</p>
                            <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-sm">Your commission</h3>
                            <p className="text-xs text-gray-500 mt-0.5 font-poppinsLight truncate max-w-[220px]">{item.title}</p>
                          </div>
                          <button
                            onClick={() => setCommissionPopupOpen(false)}
                            className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-200 transition-all border border-transparent hover:border-gray-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Popup body */}
                        <div className="p-4 space-y-3">
                          {ac && (
                            <p className="text-[10px] text-slate-400 font-medium">Values saved when this stay was added to cart.</p>
                          )}
                          {item.isOffMarketStay && om && (
                            <div className="rounded-xl border border-yellow-400/45 bg-gradient-to-r from-amber-50/95 via-yellow-50 to-amber-50/95 px-3 py-2.5 text-[10px] text-slate-800 shadow-[0_2px_10px_rgba(212,175,55,0.08)] ring-1 ring-yellow-200/50">
                              <span className="font-bold uppercase tracking-[0.18em] text-slate-500">Special commission</span>
                              <p className="mt-1.5 tabular-nums leading-snug font-medium">
                                {Number(om.specialAddedPct ?? 0) > 0 ? (
                                  <>
                                    <span className="text-slate-600">{Number(om.defaultBasePct ?? OFF_MARKET_DEFAULT_COMMISSION_PCT).toFixed(1)}%</span>
                                    <span className="mx-0.5 text-slate-400">+</span>
                                    <span className="font-semibold text-yellow-600">
                                      {Number(om.specialAddedPct ?? 0).toFixed(1)}%
                                    </span>
                                    <span className="mx-1 text-slate-300">=</span>
                                    <span className="font-bold text-slate-900">{Number(om.totalPct ?? baseline).toFixed(1)}% floor</span>
                                  </>
                                ) : (
                                  <span className="font-bold text-slate-900">
                                    {Number(om.totalPct ?? baseline).toFixed(1)}% floor
                                  </span>
                                )}
                              </p>
                            </div>
                          )}
                          <div className="border border-gray-200 bg-gray-50 px-3 py-2 text-[10px] text-gray-700">
                            <div className="flex justify-between gap-2">
                              <span>Commission floor (no extra price)</span>
                              <span className="tabular-nums font-semibold">{baseline.toFixed(1)}%</span>
                            </div>
                            <div className="flex justify-between gap-2 mt-1">
                              <span className="text-slate-600">
                                {item.isOffMarketStay ? 'Off-market stay markup' : 'Markup above floor'}
                              </span>
                              <span className="tabular-nums font-bold text-yellow-700">+{agentInc.toFixed(1)}%</span>
                            </div>
                          </div>
                          <div className="border border-gray-200 divide-y divide-gray-200 overflow-hidden bg-white">
                            <div className="flex justify-between items-center px-4 py-2.5 bg-slate-50">
                              <span className="text-xs text-slate-500">Base (booking total)</span>
                              <span className="text-xs font-semibold text-slate-700 tabular-nums">{fmt(base)}</span>
                            </div>
                            <div className="flex justify-between items-start gap-2 px-4 py-2.5 bg-white">
                              <div>
                                <span className="text-xs text-slate-500">Standard commission</span>
                                <p className="text-[10px] text-slate-400 mt-0.5">Shown for reference; not added to total below</p>
                              </div>
                              <span className="text-xs font-semibold text-slate-500 tabular-nums shrink-0">{fmt(defaultCommissionAmount)}</span>
                            </div>
                            <div className="flex justify-between items-center px-4 py-2.5 bg-slate-50">
                              <span className="text-xs font-semibold text-slate-700">Total at {pct}% (your earnings)</span>
                              <span className="text-xs font-bold text-slate-900 tabular-nums">{fmt(totalCommissionAmount)}</span>
                            </div>
                            {incrementalPct > 0 && (
                              <div className="flex justify-between items-center px-4 py-2.5 bg-white">
                                <span className="text-xs text-slate-500">
                                  Added to price ({incrementalPct.toFixed(1)}% on base above {baseline.toFixed(1)}%)
                                </span>
                                <span className="text-xs font-semibold text-slate-700 tabular-nums">+{fmt(incrementalAmount)}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center px-4 py-2.5 bg-slate-100">
                              <span className="text-xs font-bold text-slate-800">Total you pay</span>
                              <span className="text-sm font-bold text-slate-900 tabular-nums">{fmt(youPay)}</span>
                            </div>
                          </div>
                          <p className="text-[10px] text-slate-400 leading-relaxed">
                            Standard commission is not added to the total you pay. Only percentage points above{' '}
                            <span className="font-semibold text-slate-600">{baseline.toFixed(1)}%</span>
                            {item.isOffMarketStay ? ' (this hotel’s off-market minimum)' : ''} increase the payable amount. Earnings
                            are shared monthly via your My Commissions report.
                          </p>
                        </div>

                        {/* Popup footer */}
                        <div className="px-4 pb-4">
                          <button
                            onClick={() => setCommissionPopupOpen(false)}
                            className="w-full py-2.5 bg-gray-900 text-[10px] font-poppinsRegular uppercase tracking-[0.2em] text-white hover:bg-black transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Payment type badge — immediate vs scheduled */}
            {type === 'hotel' && (() => {
              const { type: payType, scheduledDate, cancellationDeadline, label } = getCartItemPaymentType(item);
              if (payType === 'scheduled') {
                return (
                  <div className="mt-3 pt-3 border-t border-blue-900/30">
                    <div className="flex items-start gap-2.5 bg-slate-900 border border-slate-800 px-3.5 py-3">
                      <CalendarClock className="w-4 h-4 text-white shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-poppinsRegular uppercase tracking-[0.25em] text-white">Scheduled Payment</span>
                          <span className="px-2 py-0.5 text-[9px] font-poppinsLight bg-slate-800 text-white border border-slate-700">
                            {label}
                          </span>
                        </div>
                        <p className="text-[11px] text-blue-50 mt-1 leading-relaxed">
                          Your card will be charged automatically on <strong>{formatPaymentDate(scheduledDate)}</strong>
                          {cancellationDeadline && (
                            <> — 72h before the free cancellation deadline (<strong>{formatPaymentDate(cancellationDeadline)}</strong>)</>
                          )}.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div className="mt-3 pt-3 border-t border-emerald-50">
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3.5 py-2.5">
                    <CreditCard className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span className="text-[10px] font-poppinsRegular uppercase tracking-[0.25em] text-emerald-700">Immediate Payment</span>
                    {/* <span className="px-2 py-0.5 text-[9px] font-poppinsLight bg-emerald-200 text-emerald-800 border border-emerald-300 ml-1">
                      Charged at checkout
                    </span> */}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-1">
      <AnimatePresence mode="wait">
        {profile && profile.length > 0 ? (
          <div className="space-y-6">
            {/* Header Section */}
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 pb-4">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-900">
                    <ShoppingCart className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h1 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-lg leading-tight">Your Cart</h1>
                    <p className="text-[9px] font-poppinsLight text-gray-400 uppercase tracking-[0.25em] mt-0.5">
                      {profile.length} Premium Selection{profile.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={onClearAll}
                disabled={deleteAllCartDeleteLoading}
                className="group inline-flex items-center gap-1.5 px-3 py-2 text-rose-500 hover:bg-rose-50 transition-all duration-200 font-poppinsRegular uppercase tracking-[0.2em] text-[10px] border border-transparent hover:border-rose-200 whitespace-nowrap shrink-0 mt-1"
              >
                <X className="h-4 w-4 transition-transform group-hover:rotate-90" />
                Clear All
              </button>
            </div>

            <div className="space-y-8">
              {/* Experiences Section */}
              {experienceItems.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100">
                      <Compass className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <h2 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-base">Curated Experiences</h2>
                      <p className="text-[9px] font-poppinsLight text-gray-500 uppercase tracking-[0.25em] mt-0.5">Authentic Bespoke Journey</p>
                    </div>
                    <div className="flex-1 h-px bg-gray-200 ml-2" />
                  </div>
                  <div className="grid gap-4">
                    {experienceItems.map((item) => (
                      <ItemCard key={item.id} item={item} type="experience" />
                    ))}
                  </div>

                  {Object.keys(experienceCurrencyTotals).length > 1 && (
                    <div className="border border-gray-200 bg-gray-50 px-4 py-3 text-[11px] text-gray-700">
                      <span className="font-bold uppercase tracking-widest text-[10px] text-slate-500 block mb-1">
                        Experience totals by currency (checkout charges each separately)
                      </span>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 font-semibold tabular-nums">
                        {Object.entries(experienceCurrencyTotals).map(([code, amt]) => (
                          <span key={code}>{formatPrice(amt, code)}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Experience Progress */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[9px] font-poppinsLight text-gray-400 uppercase tracking-[0.25em]">Client Assignment</p>
                      <p className="text-[11px] font-poppinsRegular text-gray-900">
                        {experienceItems.filter(i => selectedCustomerForItem[i.id]).length} / {experienceItems.length} Secured
                      </p>
                    </div>
                    <div className="h-1 w-full bg-gray-100 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(experienceItems.filter(i => selectedCustomerForItem[i.id]).length / experienceItems.length) * 100}%` }}
                        className="h-full bg-gray-700"
                      />
                    </div>
                  </div>

                  <button
                    onClick={onNextStepExperiences}
                    className="w-full relative group py-4 overflow-hidden bg-gray-900 transition-all duration-200 hover:bg-black mt-4 flex items-center justify-center gap-4"
                  >
                    <span className="text-[10px] font-poppinsRegular text-white tracking-[0.25em] uppercase">Initialize Experience Flow</span>
                    <ArrowRight className="h-4 w-4 text-white group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </div>
              )}

              {/* Hotels Section */}
              {hotelItems.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100">
                      <Hotel className="h-4 w-4 text-gray-700" />
                    </div>
                    <div>
                      <h2 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-base">Luxury Residences</h2>
                      <p className="text-[9px] font-poppinsLight text-gray-500 uppercase tracking-[0.25em] mt-0.5">Exclusive Refined Stays</p>
                    </div>
                    <div className="flex-1 h-px bg-gray-200 ml-2" />
                  </div>
                  <div className="grid gap-4">
                    {hotelItems.map((item) => (
                      <ItemCard key={item.id} item={item} type="hotel" />
                    ))}
                  </div>

                  {/* Hotel Progress */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[9px] font-poppinsLight text-gray-400 uppercase tracking-[0.25em]">Itinerary Verification</p>
                      <p className="text-[11px] font-poppinsRegular text-gray-900">
                        {hotelItems.filter(i => selectedCustomerForItem[i.id]).length} / {hotelItems.length} Profiles Linked
                      </p>
                    </div>
                    <div className="h-1 w-full bg-gray-100 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(hotelItems.filter(i => selectedCustomerForItem[i.id]).length / hotelItems.length) * 100}%` }}
                        className="h-full bg-gray-900"
                      />
                    </div>
                  </div>

                  {/* Payment Breakdown Panel */}
                  {(() => {
                    const immediateItems = []
                    const scheduledItems = []
                    hotelItems.forEach((item) => {
                      const pt = getCartItemPaymentType(item)
                      const totalPayable =
                        Number(
                          item?.processingFees?.totalWithFees ??
                          item?.priceDetails?.processingFees?.totalWithFees ??
                          item?.rateDetails?.processingFees?.totalWithFees ??
                          item?.totalWithProcessingFees ??
                          item?.price ??
                          0
                        ) || 0
                      const beforeFees =
                        Number(item?.totalBeforeProcessingFees ?? 0) ||
                        (Number(item?.priceDetails?.baseRate || 0) +
                          Number(item?.priceDetails?.tax || 0) +
                          Number(item?.agentCommission?.markupAmount || 0))
                      const stripeFees =
                        Number(
                          item?.processingFees?.totalProcessingFees ??
                          item?.priceDetails?.processingFees?.totalProcessingFees ??
                          item?.rateDetails?.processingFees?.totalProcessingFees ??
                          item?.totalProcessingFees ??
                          item?.priceDetails?.additionalFees ??
                          0
                        ) || 0
                      const entry = {
                        title: item.title,
                        totalPayable,
                        beforeFees,
                        stripeFees,
                        currency: item.currency || 'USD',
                        ...pt
                      }
                      if (pt.type === 'scheduled') scheduledItems.push(entry)
                      else immediateItems.push(entry)
                    })
                    const hasScheduled = scheduledItems.length > 0
                    const hasImmediate = immediateItems.length > 0
                    if (!hasImmediate && !hasScheduled) return null

                    const scheduledTotal = scheduledItems.reduce((s, i) => s + i.totalPayable, 0)
                    const immediateTotal = immediateItems.reduce((s, i) => s + i.totalPayable, 0)
                    const currency = hotelItems[0]?.currency || 'USD'
                    const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n)

                    return (
                      <div className="mt-4 border border-gray-200 overflow-hidden bg-white">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                          <p className="text-[9px] font-poppinsRegular uppercase tracking-[0.25em] text-gray-500">Payment Breakdown</p>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {hasImmediate && (
                            <div className="px-4 py-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <CreditCard className="w-3.5 h-3.5 text-emerald-600" />
                                  <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Charged Today </span>
                                </div>
                                <span className="text-sm font-bold text-slate-800">{fmt(immediateTotal)}</span>
                              </div>
                              <div className="space-y-1 pl-5">
                                {immediateItems.map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-start text-xs gap-2">
                                    <span className="text-slate-500 truncate max-w-[150px]">{item.title}</span>
                                    <div className="text-right shrink-0">
                                      <span className="font-semibold text-slate-700 block">{fmt(item.totalPayable)}</span>
                                      <span className="text-[10px] text-slate-400 font-medium block">
                                        {fmt(item.beforeFees)} + {fmt(item.stripeFees)}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {hasScheduled && (
                            <div className="px-4 py-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <CalendarClock className="w-3.5 h-3.5 text-blue-700" />
                                  <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">Scheduled Payments (Stripe)</span>
                                </div>
                                <span className="text-sm font-bold text-slate-800">{fmt(scheduledTotal)}</span>
                              </div>
                              <div className="space-y-1.5 pl-5">
                                {scheduledItems.map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-start text-xs gap-2">
                                    <span className="text-slate-500 truncate max-w-[140px]">{item.title}</span>
                                    <div className="text-right shrink-0">
                                      <span className="font-semibold text-slate-700 block">{fmt(item.totalPayable)}</span>
                                      <span className="text-[10px] text-slate-400 font-medium block">
                                        {fmt(item.beforeFees)} + {fmt(item.stripeFees)}
                                      </span>
                                      {item.scheduledDate && (
                                        <span className="text-[10px] text-blue-700 font-medium">{item.label}</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        {hasScheduled && (
                          <div className="px-4 py-2.5 bg-slate-900 border-t border-slate-800">
                            <p className="text-[11px] text-white leading-relaxed">
                              Scheduled payments are automatically charged to your saved card. You will receive a reminder email 72 hours before each charge.
                            </p>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {unavailableHotelItems.length === 0 ? (
                    <button
                      onClick={onNextStep}
                      className="w-full relative group py-4 rounded-2xl overflow-hidden bg-slate-900 shadow-xl shadow-slate-200 transition-all duration-500 hover:-translate-y-1 mt-4"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      <div className="relative flex items-center justify-center gap-4">
                        <span className="text-xs typography-body-sm font-black text-white tracking-[0.2em] uppercase">Advance to Secure Checkout</span>
                        <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-md group-hover:translate-x-2 transition-transform duration-500">
                          <ArrowRight className="h-4 w-4 text-white" />
                        </div>
                      </div>
                    </button>
                  ) : (
                    <div className="mt-4 p-4 rounded-2xl border border-amber-200 bg-amber-50/50 text-amber-800 flex flex-col gap-3">
                      {unavailableHotelItems.map((item) => {
                        const cIn = item.checkInDate || item.searchParams?.checkIn || '';
                        const cOut = item.checkOutDate || item.searchParams?.checkOut || '';
                        const ads = item.searchParams?.adults || item.maxAdults || 1;
                        const chs = item.searchParams?.children !== undefined ? item.searchParams?.children : (item.maxChildren || 0);
                        const cur = item.currency || item.searchParams?.currency || 'USD';
                        const navigateUrl = `/hotel/${item.hotelId}?checkInDate=${cIn}&checkOutDate=${cOut}&adults=${ads}&children=${chs}&currency=${cur}`;

                        return (
                          <div key={item.id} className="text-xs font-semibold flex items-center flex-wrap gap-1 leading-relaxed">
                            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mr-1" />
                            <span>Your current item "{item.title}" is expired. Remove that and select new room/rate from</span>
                            <button
                              onClick={() => navigate(navigateUrl)}
                              className="underline font-bold text-amber-900 hover:text-amber-950 transition-colors inline-flex items-center"
                            >
                              here
                            </button>
                            <span>.</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="min-h-[50vh] flex flex-col items-center justify-center p-8 text-center"
          >
            <div className="w-24 h-24 bg-gray-50 border border-gray-200 flex items-center justify-center mb-8">
              <ShoppingCart className="h-10 w-10 text-gray-300" />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-gray-300" />
              <p className="text-[9px] tracking-[0.4em] uppercase font-poppinsLight text-gray-400">Nothing here yet</p>
              <div className="w-8 h-px bg-gray-300" />
            </div>
            <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-2xl mb-3">Your Cart is Empty.</h3>
            <p className="text-sm text-gray-500 font-poppinsLight mb-8 max-w-sm mx-auto leading-relaxed">
              Begin curating a masterpiece journey for your distinguished clientele.
            </p>
            <button
              onClick={onNavigateHotels}
              className="group inline-flex items-center gap-3 px-8 py-3.5 bg-gray-900 text-white text-[10px] font-poppinsRegular uppercase tracking-[0.25em] hover:bg-black transition-all duration-200"
            >
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-x-1" />
              Start Curating
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <ClientSelectionSidebar
        isOpen={!!activeSidebarItem}
        onClose={handleCloseSidebar}
        onSelect={handleSelectClient}
        clients={filteredClients}
        selectedClientId={selectedCustomerForItem[activeSidebarItem]?.id || selectedCustomerForItem[activeSidebarItem]?._id}
        itemName={profile?.find(i => i.id === activeSidebarItem)?.title}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onShowRegistration={() => {
          onShowClientRegistration(activeSidebarItem)
          handleCloseSidebar()
        }}
      />
    </div>
  )
}

export default CartReviewStep
